import { supabase, getCurrentUserId } from "@/lib/supabase";
import { authHeaders } from "@/lib/authToken";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { embeddingService } from "@/services/embeddingService";
import {
  SHELF_EVAL_QUESTIONS,
  chunkPlainText,
  estimateCallCostUsd,
  needsSlackApproval,
  normalizeShelfTags,
  type ShelfTagBase,
} from "@shared/shelfLibrary";
import type { JobSheet } from "@shared/operatingLoop";

export type ShelfHit = {
  id: string;
  title: string;
  content: string;
  source: string;
  sourceId: string;
  shelfTags: string[];
};

async function userIdOrThrow(): Promise<string> {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error("Not signed in");
  return userId;
}

export const shelfLibraryService = {
  async logCall(input: {
    agent: string;
    model: string;
    inputTokens?: number;
    outputTokens?: number;
    cachedTokens?: number;
    status: string;
    project?: string;
  }): Promise<void> {
    const userId = await getCurrentUserId();
    if (!userId) return;
    const cost = estimateCallCostUsd({
      model: input.model,
      inputTokens: input.inputTokens ?? 0,
      outputTokens: input.outputTokens ?? 0,
      cachedTokens: input.cachedTokens ?? 0,
    });
    const { error } = await supabase.from("llm_calls").insert({
      user_id: userId,
      project: input.project ?? "deeply-smart",
      agent: input.agent,
      model: input.model,
      input_tokens: input.inputTokens ?? null,
      output_tokens: input.outputTokens ?? null,
      cached_tokens: input.cachedTokens ?? null,
      cost_usd: cost,
      status: input.status,
    });
    if (error) console.warn("[shelf] llm_calls insert failed:", error.message);
  },

  async overMonthlyBudget(project = "deeply-smart"): Promise<boolean> {
    const userId = await getCurrentUserId();
    if (!userId) return false;
    const { data: budget } = await supabase
      .from("llm_project_budgets")
      .select("monthly_budget_usd")
      .eq("user_id", userId)
      .eq("project", project)
      .maybeSingle();
    const cap = Number(budget?.monthly_budget_usd ?? 0);
    if (!cap) return false;
    const start = new Date();
    start.setUTCDate(1);
    start.setUTCHours(0, 0, 0, 0);
    const { data: calls } = await supabase
      .from("llm_calls")
      .select("cost_usd")
      .eq("user_id", userId)
      .eq("project", project)
      .gte("created_at", start.toISOString());
    const spent = (calls ?? []).reduce((sum, row) => sum + Number(row.cost_usd ?? 0), 0);
    return spent >= cap;
  },

  async search(query: string, tags: string[] = ["general"]): Promise<ShelfHit[]> {
    const headers = await authHeaders();
    const res = await fetch("/api/embeddings?route=shelf-search", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ query, tags: normalizeShelfTags(tags), limit: 5, agent: "librarian" }),
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { hits?: Array<Record<string, unknown>> };
    return (body.hits ?? []).map((hit) => ({
      id: String(hit.id ?? ""),
      title: String(hit.title ?? ""),
      content: String(hit.content ?? ""),
      source: String(hit.source ?? ""),
      sourceId: String(hit.sourceId ?? ""),
      shelfTags: Array.isArray(hit.shelfTags) ? hit.shelfTags.map(String) : [],
    }));
  },

  async requestSlackGate(sheet: JobSheet): Promise<{ approved: boolean; message: string }> {
    const userId = await userIdOrThrow();
    const tags = normalizeShelfTags(sheet.shelves_used.filter((id) => id.includes(":") === false));
    const sensitive = needsSlackApproval(sheet.shelves_used.some((id) => id.startsWith("client:")) ? ["client:shelf"] : tags, sheet.sensitivity);
    if (!sensitive) return { approved: true, message: "" };
    const { data: existing } = await supabase
      .from("operating_jobs")
      .select("id, status")
      .eq("user_id", userId)
      .eq("status", "approved")
      .contains("work_order", { request: sheet.request })
      .limit(1);
    if (existing && existing.length > 0) return { approved: true, message: "" };
    const { data: inserted } = await supabase
      .from("operating_jobs")
      .insert({
        user_id: userId,
        status: "awaiting_slack",
        work_order: sheet,
        attempts: 1,
      })
      .select("id")
      .single();
    const headers = await authHeaders();
    await fetch("/api/workflows/automation?action=shelf-slack", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ jobId: inserted?.id, request: sheet.request, goal: sheet.goal, sensitivity: sheet.sensitivity }),
    }).catch(() => undefined);
    return {
      approved: false,
      message: "This work order is waiting for Slack approval before a model is called.",
    };
  },

  async approveJob(jobId: string): Promise<void> {
    const userId = await userIdOrThrow();
    const { error } = await supabase
      .from("operating_jobs")
      .update({ status: "approved", updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("id", jobId);
    if (error) throw new Error(error.message);
  },

  async writeOutcome(sheet: JobSheet, answer: string): Promise<void> {
    const userId = await getCurrentUserId();
    if (!userId || !answer.trim()) return;
    const tags = normalizeShelfTags(["general"]);
    const sourceId = `work-order:${Date.now()}`;
    const { error } = await supabase.from("shelf_documents").insert({
      user_id: userId,
      source: "work_order",
      source_id: sourceId,
      title: sheet.goal.slice(0, 180) || sheet.request.slice(0, 180),
      content: answer.trim(),
      chunk_index: 0,
      shelf_tags: tags,
      sensitivity: sheet.sensitivity === "client" || sheet.sensitivity === "restricted" ? sheet.sensitivity : "general",
      updated_at: new Date().toISOString(),
    });
    if (error) console.warn("[shelf] write-back failed:", error.message);
    try {
      await knowledgeBaseService.createKnowledgeBaseItem({
        title: `Work order: ${sheet.goal.slice(0, 80)}`,
        content: answer.trim(),
      });
    } catch (libraryError) {
      console.warn("[shelf] library note failed:", libraryError);
    }
  },

  async ingestLibrarySpace(spaceId: string): Promise<{ chunks: number }> {
    const userId = await userIdOrThrow();
    const { data, error } = await supabase
      .from("knowledgebase")
      .select('id, title, content, "spaceId", metadata')
      .eq("userId", userId)
      .limit(80);
    if (error) throw new Error(error.message);
    const linked = (data ?? []).filter((item) => {
      const meta = item.metadata as { spaceId?: string; spaceIds?: string[] } | null;
      if (item.spaceId != null && String(item.spaceId) === spaceId) return true;
      if (meta?.spaceId != null && String(meta.spaceId) === spaceId) return true;
      return Array.isArray(meta?.spaceIds) && meta.spaceIds.some((id) => String(id) === spaceId);
    });
    let chunks = 0;
    for (const item of linked.slice(0, 12)) {
      const parts = chunkPlainText(typeof item.content === "string" ? item.content : "", 2000);
      for (let index = 0; index < parts.length; index += 1) {
        let embedding: number[] | null = null;
        try {
          const embedded = await embeddingService.generateEmbedding(parts[index].slice(0, 2000));
          embedding = embedded.embedding;
        } catch (embedError) {
          console.warn("[shelf] embed failed:", embedError);
        }
        const { error: upsertError } = await supabase.from("shelf_documents").upsert(
          {
            user_id: userId,
            source: "library",
            source_id: String(item.id),
            title: item.title || "Note",
            content: parts[index],
            chunk_index: index,
            embedding,
            shelf_tags: ["general"],
            sensitivity: "general",
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,source,source_id,chunk_index" },
        );
        if (!upsertError) chunks += 1;
      }
    }
    return { chunks };
  },

  async mirrorIndexedSource(source: "clickup" | "drive"): Promise<{ chunks: number }> {
    const userId = await userIdOrThrow();
    const prior = await supabase
      .from("shelf_eval_runs")
      .select("id")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1);
    if (prior.error) throw new Error(prior.error.message);
    if (!prior.data?.length) throw new Error("Run the shelf eval on the first source before adding another.");
    let chunks = 0;
    if (source === "clickup") {
      const { data } = await supabase
        .from("integration_chunks")
        .select("external_id, title, chunk_text, chunk_index")
        .eq("user_id", userId)
        .eq("source", "clickup")
        .limit(30);
      for (const row of data ?? []) {
        const { error } = await supabase.from("shelf_documents").upsert(
          {
            user_id: userId,
            source: "clickup",
            source_id: String(row.external_id),
            title: String(row.title ?? "ClickUp"),
            content: String(row.chunk_text ?? ""),
            chunk_index: Number(row.chunk_index ?? 0),
            shelf_tags: ["operations"],
            sensitivity: "general",
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,source,source_id,chunk_index" },
        );
        if (!error) chunks += 1;
      }
    } else {
      const { data: docs } = await supabase
        .from("knowledgebase")
        .select('id, title, content, metadata, "googleDocsId"')
        .eq("userId", userId)
        .limit(40);
      const driveDocs = (docs ?? []).filter((item) => {
        const meta = item.metadata as { googleDrive?: { fileId?: string } } | null;
        return Boolean(meta?.googleDrive?.fileId || item.googleDocsId);
      });
      for (const item of driveDocs.slice(0, 20)) {
        const parts = chunkPlainText(typeof item.content === "string" ? item.content : "", 2000);
        const part = parts[0];
        if (!part) continue;
        const { error } = await supabase.from("shelf_documents").upsert(
          {
            user_id: userId,
            source: "drive",
            source_id: String(item.id),
            title: item.title || "Drive doc",
            content: part,
            chunk_index: 0,
            shelf_tags: ["operations"],
            sensitivity: "general",
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,source,source_id,chunk_index" },
        );
        if (!error) chunks += 1;
      }
    }
    return { chunks };
  },

  async runEval(source = "library"): Promise<{ questions: number; hits: number; hitRate: number }> {
    const userId = await userIdOrThrow();
    let hits = 0;
    for (const question of SHELF_EVAL_QUESTIONS) {
      const found = await this.search(question, ["general"]);
      if (found.length > 0) hits += 1;
    }
    const hitRate = hits / SHELF_EVAL_QUESTIONS.length;
    await supabase.from("shelf_eval_runs").insert({
      user_id: userId,
      source,
      questions: SHELF_EVAL_QUESTIONS.length,
      hits,
      hit_rate: hitRate,
    });
    return { questions: SHELF_EVAL_QUESTIONS.length, hits, hitRate };
  },

  async saveBaseline(input: { provider: "claude" | "gpt" | "rok"; spendUsd: number; capHits: number; note: string }): Promise<void> {
    const userId = await userIdOrThrow();
    const week = new Date();
    const day = week.getUTCDay();
    week.setUTCDate(week.getUTCDate() - day);
    const weekStart = week.toISOString().slice(0, 10);
    const { error } = await supabase.from("llm_spend_baselines").upsert(
      {
        user_id: userId,
        provider: input.provider,
        week_start: weekStart,
        spend_usd: input.spendUsd,
        cap_hits: input.capHits,
        note: input.note,
      },
      { onConflict: "user_id,provider,week_start" },
    );
    if (error) throw new Error(error.message);
  },

  async saveBudget(monthlyBudgetUsd: number, maxSeatsHumansOnly: boolean): Promise<void> {
    const userId = await userIdOrThrow();
    const { error } = await supabase.from("llm_project_budgets").upsert(
      {
        user_id: userId,
        project: "deeply-smart",
        monthly_budget_usd: monthlyBudgetUsd,
        max_seats_humans_only: maxSeatsHumansOnly,
      },
      { onConflict: "user_id,project" },
    );
    if (error) throw new Error(error.message);
  },

  async report(): Promise<{
    baselineUsd: number;
    spentUsd: number;
    calls: number;
    decision: string | null;
    budgetAlert: boolean;
  }> {
    const userId = await userIdOrThrow();
    const [baselines, calls, decision, budget] = await Promise.all([
      supabase.from("llm_spend_baselines").select("spend_usd").eq("user_id", userId),
      supabase.from("llm_calls").select("cost_usd").eq("user_id", userId),
      supabase.from("synthesis_decisions").select("decision").eq("user_id", userId).order("created_at", { ascending: false }).limit(1),
      supabase.from("llm_project_budgets").select("monthly_budget_usd").eq("user_id", userId).eq("project", "deeply-smart").maybeSingle(),
    ]);
    const baselineUsd = (baselines.data ?? []).reduce((sum, row) => sum + Number(row.spend_usd ?? 0), 0);
    const spentUsd = (calls.data ?? []).reduce((sum, row) => sum + Number(row.cost_usd ?? 0), 0);
    const cap = Number(budget.data?.monthly_budget_usd ?? 0);
    return {
      baselineUsd,
      spentUsd,
      calls: calls.data?.length ?? 0,
      decision: decision.data?.[0]?.decision ?? null,
      budgetAlert: cap > 0 && spentUsd >= cap * 0.1,
    };
  },

  async recordDecision(decision: "keep_hosted" | "self_host", reason: string): Promise<void> {
    const userId = await userIdOrThrow();
    const { error } = await supabase.from("synthesis_decisions").insert({ user_id: userId, decision, reason });
    if (error) throw new Error(error.message);
  },

  async saveRouting(fastMaxChars: number): Promise<void> {
    const userId = await userIdOrThrow();
    const { error } = await supabase.from("llm_routing_config").upsert(
      { user_id: userId, fast_max_chars: fastMaxChars, updated_at: new Date().toISOString() },
      { onConflict: "user_id" },
    );
    if (error) throw new Error(error.message);
  },

  async fastMaxChars(): Promise<number> {
    const userId = await getCurrentUserId();
    if (!userId) return 120;
    const { data } = await supabase.from("llm_routing_config").select("fast_max_chars").eq("user_id", userId).maybeSingle();
    const value = Number(data?.fast_max_chars ?? 120);
    return Number.isFinite(value) && value > 0 ? value : 120;
  },

  async pendingJobs(): Promise<Array<{ id: string; status: string; request: string }>> {
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data } = await supabase
      .from("operating_jobs")
      .select("id, status, work_order")
      .eq("user_id", userId)
      .eq("status", "awaiting_slack")
      .order("created_at", { ascending: false })
      .limit(10);
    return (data ?? []).map((row) => ({
      id: String(row.id),
      status: String(row.status),
      request: String((row.work_order as { request?: string } | null)?.request ?? ""),
    }));
  },
};

export type { ShelfTagBase };
